/**
 * Component: Complementary Product Card
 * -----------------------------------------------------------------------------
 * PDP complementary card: variant <select> + inline Add. Dropdown updates
 * image/price; Add posts the selected variant to /cart/add.js.
 *
 * Upcart: pause its fetch interceptor, refresh header only, then
 * upcartRefreshCart + upcartOpenCart. Do not merge native cart-drawer
 * (cc-cart-cross-sell.init is undefined) or dispatch on:cart:change /
 * on:cart:add.
 *
 * Native drawer: merge cart-drawer HTML, then open.
 *
 * ## Inner selectors:
 *  - [data-js-variant-select] — <select> or hidden input
 *  - [data-js-add]            — Add button
 *  - [data-js-image]          — Preview image (data-media-id)
 *  - [data-js-price]          — Price container
 *  - [data-js-product-link]   — Links kept in sync with the variant
 */

(() => {
  if (customElements.get('complementary-product-card')) {
    return;
  }

  class ComplementaryProductCard extends HTMLElement {
    static _selectors = {
      variantSelect: '[data-js-variant-select]',
      addButton: '[data-js-add]',
      image: '[data-js-image]',
      price: '[data-js-price]',
      productLink: '[data-js-product-link]',
    };

    constructor() {
      super();

      this.onSelectChange = this.handleSelectChange.bind(this);
      this.onAddClick = this.handleAddClick.bind(this);
    }

    connectedCallback() {
      this.initialize();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    initialize() {
      const { _selectors } = ComplementaryProductCard;

      this.select = this.querySelector(_selectors.variantSelect);
      this.addButton = this.querySelector(_selectors.addButton);
      this.priceContainer = this.querySelector(_selectors.price);
      this.images = this.querySelectorAll(_selectors.image);
      this.productLinks = this.querySelectorAll(_selectors.productLink);
      this.addEventListeners();
    }

    cleanup() {
      this.removeEventListeners();
    }

    addEventListeners() {
      this.select?.addEventListener('change', this.onSelectChange);
      this.addButton?.addEventListener('click', this.onAddClick);
    }

    removeEventListeners() {
      this.select?.removeEventListener('change', this.onSelectChange);
      this.addButton?.removeEventListener('click', this.onAddClick);
    }

    /**
     * Sync image, price, and product links when the selected variant changes.
     * @param {Event} evt - Change event from the variant select
     */
    handleSelectChange(evt) {
      evt.stopPropagation();

      const option = this.getSelectedOption();

      if (!option) {
        return;
      }

      this.setActiveImage(option.dataset.mediaId);
      this.updatePrice(option);
      this.updateProductLinks(option.value);
    }

    /**
     * Add the currently selected variant to the cart.
     * @param {Event} evt - Click event from the Add button
     */
    async handleAddClick(evt) {
      evt.preventDefault();
      evt.stopPropagation();

      if (this.addButton?.disabled) {
        return;
      }

      const variantId = this.getSelectedOption()?.value || this.select?.value;

      if (!variantId) {
        return;
      }

      const upcart = Boolean(document.getElementById('upCart'));
      const previousSkip = window.upcartShouldSkipAddToCartInterceptor;
      const sections = [document.querySelector('page-header')];

      if (!upcart) {
        sections.push(document.querySelector('cart-drawer'));
      }

      const toUpdate = sections.filter((el) => el?.dataset.sectionId);

      this.setBusy(true);
      window.upcartShouldSkipAddToCartInterceptor = true;

      try {
        const data = await this.addVariantToCart(variantId, toUpdate);

        toUpdate.forEach((el) => {
          const html = data.sections?.[el.dataset.sectionId];

          if (html) {
            el.updateFromCartChange?.(html);
          }
        });

        if (upcart) {
          await window.upcartRefreshCart?.();
          window.upcartOpenCart?.();
        } else if (theme.settings.afterAddToCart === 'drawer') {
          document.querySelector('.js-cart-drawer')?.open();
        }
      } catch (error) {
        console.error('[ComplementaryProductCard] Failed to add to cart:', error);
      } finally {
        window.upcartShouldSkipAddToCartInterceptor = previousSkip;
        this.setBusy(false);
      }
    }

    /**
     * @param {string} variantId
     * @param {HTMLElement[]} sections
     * @returns {Promise<object>}
     */
    async addVariantToCart(variantId, sections) {
      const body = {
        id: variantId,
        quantity: 1,
      };

      if (sections.length) {
        body.sections = sections.map((el) => el.dataset.sectionId).join(',');
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || response.status);
      }

      return data;
    }

    /**
     * @returns {HTMLOptionElement|null}
     */
    getSelectedOption() {
      if (this.select?.tagName !== 'SELECT') {
        return null;
      }

      return this.select.options[this.select.selectedIndex] || null;
    }

    /**
     * Show the preview image that matches the selected variant's media.
     * @param {string} mediaId - Shopify media ID
     */
    setActiveImage(mediaId) {
      if (!mediaId || !this.images.length) {
        return;
      }

      let matched = false;

      this.images.forEach((image) => {
        const isMatch = image.dataset.mediaId === String(mediaId);

        image.classList.toggle('product-block__image--active', isMatch);

        if (isMatch) {
          matched = true;
        }
      });

      if (!matched) {
        this.images[0].classList.add('product-block__image--active');
      }
    }

    /**
     * Update the visible price to match the selected variant.
     * @param {HTMLOptionElement} option - Selected variant option
     */
    updatePrice(option) {
      if (!this.priceContainer || !option) {
        return;
      }

      const currentEl = this.priceContainer.querySelector('.price__current');
      const wasEl = this.priceContainer.querySelector('.price__was');
      const priceEl = this.priceContainer.querySelector('.price');
      const compareAt = option.dataset.compareAt || '';
      const onSale = compareAt !== '';

      if (currentEl) {
        currentEl.textContent = option.dataset.price || '';
        currentEl.classList.toggle('sale-price', onSale);
      }

      if (wasEl) {
        wasEl.textContent = compareAt;
      }

      priceEl?.classList.toggle('price--on-sale', onSale);
    }

    /**
     * Keep product links pointing at the selected variant.
     * @param {string} variantId - Shopify variant ID
     */
    updateProductLinks(variantId) {
      if (!variantId) {
        return;
      }

      this.productLinks.forEach((link) => {
        const url = new URL(link.href, window.location.origin);

        url.searchParams.set('variant', variantId);
        link.href = `${url.pathname}${url.search}`;
      });
    }

    /**
     * Toggle the Add button loading/disabled state.
     * @param {boolean} busy - Whether a cart request is in flight
     */
    setBusy(busy) {
      if (!this.addButton) {
        return;
      }

      this.addButton.disabled = busy;
      this.addButton.setAttribute('aria-busy', String(busy));
      this.addButton.classList.toggle('is-loading', busy);
    }
  }

  customElements.define('complementary-product-card', ComplementaryProductCard);
})();
